import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText,
  MessageSquare,
  Mic,
  Table,
  Presentation as PresentationIcon,
  MoreHorizontal,
} from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '@/store/appStore';
import { InlineEditInput } from './InlineEditInput';
import { handleError } from '@/utils/errorHandler';

interface SortableGenericItemProps {
  item: { id: string; name: string; type: string };
  isActive: boolean;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}

export function SortableGenericItem({
  item,
  isActive,
  onClick,
  onContextMenu,
}: SortableGenericItemProps) {
  const editingItems = useAppStore((state) => state.editingItems);
  const savePendingItemName = useAppStore((state) => state.savePendingItemName);
  const setEditingItem = useAppStore((state) => state.setEditingItem);
  const projectItems = useAppStore((state) => state.projectItems);
  const updateSpaceItems = useAppStore((state) => state.updateSpaceItems);
  const isEditing = editingItems.has(item.id);
  
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `item-${item.id}`,
    data: {
      type: 'item',
      itemType: item.type,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const iconMap = {
    document: FileText,
    chat: MessageSquare,
    voice_channel: Mic,
    spreadsheet: Table,
    presentation: PresentationIcon,
  };
  
  const placeholderMap = {
    document: 'Nome documento...',
    chat: 'Nome chat...',
    voice_channel: 'Nome canale vocale...',
    spreadsheet: 'Nome foglio...',
    presentation: 'Nome presentazione...',
  };

  const Icon = iconMap[item.type as keyof typeof iconMap] || FileText;
  const placeholder = placeholderMap[item.type as keyof typeof placeholderMap] || 'Nome...';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isEditing ? {} : listeners)} // Don't apply drag listeners when editing
      onClick={isEditing ? undefined : onClick}
      onContextMenu={onContextMenu}
      className={clsx(
        'w-full flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] transition-colors select-none group',
        isEditing ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
        isActive
          ? 'bg-accent-violet-dim text-accent-violet'
          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
      )}
    >
      <Icon className="w-2.5 h-2.5 shrink-0" />
      {isEditing ? (
        <InlineEditInput
          value={item.name}
          placeholder={placeholder}
          onSave={async (name) => {
            try {
              await savePendingItemName(item.id, name);
            } catch (error) {
              handleError(error, { level: 'error', message: 'Errore nel salvataggio del nome dell\'elemento' });
              setEditingItem(item.id, false);
            }
          }}
          onCancel={() => {
            setEditingItem(item.id, false);
            // Remove temporary item if cancelled
            Object.keys(projectItems).forEach(spaceName => {
              const spaceItems = projectItems[spaceName] || [];
              const updatedItems = spaceItems.filter(i => i.id !== item.id);
              if (updatedItems.length !== spaceItems.length) {
                updateSpaceItems(spaceName, updatedItems);
              }
            });
          }}
          className="flex-1"
        />
      ) : (
        <>
      <span className="flex-1 text-left truncate cursor-pointer">{item.name}</span>
      {/* Menu — appears on hover, consistent with list/folder items */}
      {onContextMenu && (
        <span className="relative flex items-center justify-end min-w-[16px]">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onContextMenu(e);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="absolute inset-0 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-surface-tertiary transition-all"
          >
            <MoreHorizontal className="w-2.5 h-2.5 text-text-quaternary" />
          </button>
        </span>
          )}
        </>
      )}
    </div>
  );
}
